import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useState } from "react";
import API, { APIError, User } from "snudbud-backbone-sdk";
import { DataReturn } from "snudbud-backbone-sdk/dist/api/APIBase";
import * as config from "../config";
import handleError from "../utils/handleError";

export interface APIContextType {
  api: API;
  loading: boolean;
  updateUser: (load?: boolean) => Promise<void>;
  user?: User.FetchReturn;
  userProfile?: User.FetchProfileReturn;
  staticData?: DataReturn;
}

const APIContext = React.createContext<APIContextType | null>(null);

// Use development API in debug mode, production API otherwise
const baseURL = config.DEBUG ? "http://192.168.1.66:7200" : "https://api.pimaru.com";

// Initialize the API with configuration
const api = new API({
  baseURL,
  debug: config.BENCHMARK,
  reauthenticate: true,
  socket: true,
});

/**
 * Provides API access and authentication state management
 */
const APIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User.FetchReturn>();
  const [staticData, setStaticData] = useState<DataReturn>();
  const [userProfile, setUserProfile] = useState<User.FetchProfileReturn>();

  const posthog = usePostHog();

  const fetchUser = useCallback(async () => {
    const userResponse = await api.user.fetch();
    setUser(userResponse);
    return userResponse;
  }, [setUser]);

  const updateUserProfile = useCallback(
    async (userId?: string) => {
      if (!userId && !user?.userId) return undefined;

      try {
        const targetUserId = userId || user?.userId;
        const profileData = await api.user.fetchProfile({ userId: targetUserId! });

        // Only set as cached profile if it's the current user
        if (targetUserId === user?.userId) {
          setUserProfile(profileData);
        }

        return profileData;
      } catch (error) {
        handleError(error);
        return undefined;
      }
    },
    [user?.userId]
  );

  const updateUser = useCallback(
    async (load: boolean = false) => {
      if (load) setLoading(true);

      try {
        const userData = await fetchUser();
        if (userData?.userId) {
          await updateUserProfile(userData.userId);
        }
      } catch (e) {
        setUser(undefined);
        setUserProfile(undefined);
      } finally {
        if (load) setLoading(false);
      }
    },
    [fetchUser, updateUserProfile]
  );

  useEffect(() => {
    (async () => {
      setStaticData(await api.data());

      const refreshToken = localStorage.getItem("refresh-token");
      if (!refreshToken) return setLoading(false);

      try {
        await api.reauthenticate({ refreshToken });
      } catch (err: any) {
        setLoading(false);

        if (
          [
            APIError.UnauthorizedCode.RefreshTokenInvalid,
            APIError.UnauthorizedCode.SessionInvalid,
            APIError.ForbiddenCode.UserBanned,
          ].includes(err.response?.data?.code)
        ) {
          localStorage.removeItem("refresh-token");
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;

    // Load user profile after user data is loaded
    if (user.userId) {
      updateUserProfile(user.userId).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, updateUserProfile]);

  api.onAccessTokenChange = async (accessToken: string | null) => {
    if (!accessToken) {
      setUser(undefined);
      setUserProfile(undefined);
      setLoading(false);
      return;
    }

    try {
      const userData = await fetchUser();
      if (!userData?.userId) return;

      posthog.identify(userData.userId);
      await updateUserProfile(userData.userId);
    } catch (err) {
      localStorage.removeItem("refresh-token");
      setLoading(false);
      handleError(err);
    }
  };

  api.onRefreshTokenChange = (refreshToken: string | null) => {
    if (!refreshToken) localStorage.removeItem("refresh-token");
    else localStorage.setItem("refresh-token", refreshToken);
  };

  const value = { api, loading, updateUser, user, userProfile, staticData };

  return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
};

/**
 * Hook to access the API and authentication state
 */
export const useAPI = () => {
  const context = React.useContext(APIContext);
  if (context === null) throw new Error("useAPI must be used within an APIProvider");

  return context;
};

export default APIProvider;
